"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ArrowDownUp, ChevronDown, CircleAlert, Gavel, History, ListFilter, SearchX } from "lucide-react";
import { SearchField } from "@/features/home-v2/browse/BrowseControls";
import DateField from "@/features/home-v2/browse/DateField";
import SortMenu from "@/features/home-v2/browse/SortMenu";
import { DeskBar, DeskCover, DeskEmpty, DeskMore, DeskSeg } from "@/features/home-v2/desk/Desk";
import { RouteHero, RouteHeroMark } from "@/features/home-v2/RouteHero";
import { Loading } from "@/features/home-v2/ui";
import { useScrollReveal } from "@/features/home-v2/useScrollReveal";
import MyBidRow from "./MyBidRow";
import { useMyBids, type BidSortValue, type BidStatusTab } from "./useMyBids";
import "./my-bids.css";

/**
 * «پیشنهادهای من» — the viewer's bids, in v2: a ruled ledger, on its own
 * route (`/bids`) under a route hero and inside the profile's owner tab
 * (`embedded`) under its own cover. Every rule is MyBidsClient's (`useMyBids`).
 */
export default function MyBids(t0) {
  const $ = _c(235);
  const {
    lang,
    embedded: t1
  } = t0;
  const embedded = t1 === undefined ? false : t1;
  const isFa = lang === "fa";
  let t2;
  if ($[0] !== isFa) {
    t2 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t = t2;
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const m = useMyBids(lang);
  const f = m.filters;
  const t3 = m.visible.length;
  const t4 = m.canFetchMore;
  const t5 = m.fetchMore;
  const t6 = m.isFetchingMore;
  let t7;
  if ($[2] !== f.auctionId || $[3] !== f.currencyId || $[4] !== f.fromDate || $[5] !== f.sortBy || $[6] !== f.tab || $[7] !== f.toDate || $[8] !== f.winnerFilter) {
    t7 = JSON.stringify([f.auctionId, f.currencyId, f.winnerFilter, f.fromDate, f.toDate, f.tab, f.sortBy]);
    $[2] = f.auctionId;
    $[3] = f.currencyId;
    $[4] = f.fromDate;
    $[5] = f.sortBy;
    $[6] = f.tab;
    $[7] = f.toDate;
    $[8] = f.winnerFilter;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== m.canFetchMore || $[11] !== m.fetchMore || $[12] !== m.isFetchingMore || $[13] !== m.visible.length || $[14] !== t7) {
    t8 = {
      loaded: t3,
      canFetchMore: t4,
      fetchMore: t5,
      isFetching: t6,
      resetKey: t7
    };
    $[10] = m.canFetchMore;
    $[11] = m.fetchMore;
    $[12] = m.isFetchingMore;
    $[13] = m.visible.length;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const reveal = useScrollReveal(t8);
  const loaded = t("\u062F\u0631 \u0646\u0645\u0627\u06CC \u0641\u0639\u0644\u06CC", "in this view");
  const tally = [{
    key: "view",
    label: t("\u0646\u0645\u0627\u06CC \u0641\u0639\u0644\u06CC", "In view"),
    value: nf.format(m.stats.total),
    tone: "accent" as const
  }, {
    key: "pending",
    label: t("\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631", "Pending"),
    value: nf.format(m.stats.pending),
    tone: "muted" as const,
    note: loaded
  }, {
    key: "accepted",
    label: t("\u062A\u0623\u06CC\u06CC\u062F\u0634\u062F\u0647", "Accepted"),
    value: nf.format(m.stats.accepted),
    tone: "up" as const,
    note: loaded
  }, {
    key: "won",
    label: t("\u0628\u0631\u062F\u0647\u200C\u0634\u062F\u0647", "Won"),
    value: nf.format(m.stats.won),
    tone: "gold" as const,
    note: loaded
  }];
  const count = n => n === null ? null : nf.format(n);
  const tabs = [{
    key: "all",
    label: t("\u0647\u0645\u0647", "All"),
    count: count(m.counts.all)
  }, {
    key: "pending",
    label: t("\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631", "Pending")
  }, {
    key: "accepted",
    label: t("\u062A\u0623\u06CC\u06CC\u062F\u0634\u062F\u0647", "Accepted")
  }, {
    key: "rejected",
    label: t("\u0631\u062F\u0634\u062F\u0647", "Rejected")
  }, {
    key: "won",
    label: t("\u0628\u0631\u0646\u062F\u0647", "Won"),
    count: count(m.counts.won)
  }];
  let t9;
  if ($[16] !== t) {
    t9 = t("\u0647\u0645\u0647\u200C\u06CC \u0646\u062A\u06CC\u062C\u0647\u200C\u0647\u0627", "Any result");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = {
      key: "all",
      label: t9
    };
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t) {
    t11 = t("\u0641\u0642\u0637 \u0628\u0631\u0646\u062F\u0647\u200C\u0647\u0627", "Winners only");
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = {
      key: "true",
      label: t11
    };
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t) {
    t13 = t("\u063A\u06CC\u0631\u0628\u0631\u0646\u062F\u0647\u200C\u0647\u0627", "Non-winners");
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = {
      key: "false",
      label: t13
    };
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t10 || $[29] !== t12 || $[30] !== t14) {
    t15 = [t10, t12, t14];
    $[28] = t10;
    $[29] = t12;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const winOptions = t15;
  let body;
  if (m.isLoading) {
    let t16;
    if ($[32] !== t) {
      t16 = t("\u062F\u0631 \u062D\u0627\u0644 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627", "Loading your bids");
      $[32] = t;
      $[33] = t16;
    } else {
      t16 = $[33];
    }
    let t17;
    if ($[34] !== t16) {
      t17 = <Loading label={t16} />;
      $[34] = t16;
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    body = t17;
  } else {
    if (m.isError) {
      let t16;
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = <CircleAlert />;
        $[36] = t16;
      } else {
        t16 = $[36];
      }
      let t17;
      if ($[37] !== t) {
        t17 = t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0646\u0634\u062F", "Couldn't load your bids");
        $[37] = t;
        $[38] = t17;
      } else {
        t17 = $[38];
      }
      let t18;
      if ($[39] !== t) {
        t18 = t("\u0627\u0631\u062A\u0628\u0627\u0637 \u0628\u0627 \u0633\u0631\u0648\u0631 \u0628\u0631\u0642\u0631\u0627\u0631 \u0646\u0634\u062F. \u0686\u0646\u062F \u0644\u062D\u0638\u0647 \u0628\u0639\u062F \u062F\u0648\u0628\u0627\u0631\u0647 \u062A\u0644\u0627\u0634 \u06A9\u0646\u06CC\u062F.", "We couldn't reach the server. Try again in a moment.");
        $[39] = t;
        $[40] = t18;
      } else {
        t18 = $[40];
      }
      const t19 = m.retry;
      let t20;
      if ($[41] !== t) {
        t20 = t("\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647", "Try again");
        $[41] = t;
        $[42] = t20;
      } else {
        t20 = $[42];
      }
      let t21;
      if ($[43] !== m.retry || $[44] !== t20) {
        t21 = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={t19}>{t20}</button>;
        $[43] = m.retry;
        $[44] = t20;
        $[45] = t21;
      } else {
        t21 = $[45];
      }
      let t22;
      if ($[46] !== t17 || $[47] !== t18 || $[48] !== t21) {
        t22 = <DeskEmpty tone="error" icon={t16} title={t17} body={t18} actions={t21} />;
        $[46] = t17;
        $[47] = t18;
        $[48] = t21;
        $[49] = t22;
      } else {
        t22 = $[49];
      }
      body = t22;
    } else {
      if (m.bids.length === 0) {
        let t16;
        if ($[50] !== f.filtered || $[51] !== f.reset || $[52] !== lang || $[53] !== t) {
          t16 = f.filtered ? <DeskEmpty tone="filter" icon={<SearchX />} title={t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u06CC \u0628\u0627 \u0627\u06CC\u0646 \u0641\u06CC\u0644\u062A\u0631\u0647\u0627 \u067E\u06CC\u062F\u0627 \u0646\u0634\u062F", "No bids match these filters")} body={t("\u0634\u0646\u0627\u0633\u0647\u200C\u06CC \u0645\u0632\u0627\u06CC\u062F\u0647\u060C \u0627\u0631\u0632\u060C \u0646\u062A\u06CC\u062C\u0647 \u06CC\u0627 \u0628\u0627\u0632\u0647\u200C\u06CC \u062A\u0627\u0631\u06CC\u062E \u0631\u0627 \u062A\u063A\u06CC\u06CC\u0631 \u062F\u0647\u06CC\u062F\u060C \u06CC\u0627 \u0647\u0645\u0647\u200C\u06CC \u0641\u06CC\u0644\u062A\u0631\u0647\u0627 \u0631\u0627 \u067E\u0627\u06A9 \u06A9\u0646\u06CC\u062F.", "Change the auction ID, currency, result or dates \u2014 or clear every filter.")} actions={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={f.reset}>{t("\u067E\u0627\u06A9 \u06A9\u0631\u062F\u0646 \u0641\u06CC\u0644\u062A\u0631\u0647\u0627", "Clear filters")}</button>} /> : <DeskEmpty icon={<History />} title={t("\u0647\u0646\u0648\u0632 \u0631\u0648\u06CC \u0645\u0632\u0627\u06CC\u062F\u0647\u200C\u0627\u06CC \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0646\u062F\u0627\u062F\u0647\u200C\u0627\u06CC\u062F", "You haven't bid on an auction yet")} body={t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u06CC \u06A9\u0647 \u0631\u0648\u06CC \u0647\u0631 \u0645\u0632\u0627\u06CC\u062F\u0647 \u0628\u062F\u0647\u06CC\u062F \u0627\u06CC\u0646\u200C\u062C\u0627 \u062B\u0628\u062A \u0645\u06CC\u200C\u0634\u0648\u062F\u060C \u0628\u0627 \u0645\u0628\u0644\u063A\u060C \u0648\u0636\u0639\u06CC\u062A \u0648 \u0646\u062A\u06CC\u062C\u0647\u200C\u0627\u0634.", "Every bid you place lands here, with its amount, status and outcome.")} actions={<Link className="v2-btn v2-focus" href={`/${lang}/active-bid`}><Gavel aria-hidden={true} />{t("\u062F\u06CC\u062F\u0646 \u0645\u0632\u0627\u06CC\u062F\u0647\u200C\u0647\u0627\u06CC \u0628\u0627\u0632", "See open auctions")}</Link>} />;
          $[50] = f.filtered;
          $[51] = f.reset;
          $[52] = lang;
          $[53] = t;
          $[54] = t16;
        } else {
          t16 = $[54];
        }
        body = t16;
      } else {
        if (m.visible.length === 0) {
          let t16;
          if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
            t16 = <ListFilter />;
            $[55] = t16;
          } else {
            t16 = $[55];
          }
          let t17;
          if ($[56] !== t) {
            t17 = t("\u062F\u0631 \u0627\u06CC\u0646 \u062F\u0633\u062A\u0647 \u0645\u0648\u0631\u062F\u06CC \u0646\u06CC\u0633\u062A", "Nothing in this view");
            $[56] = t;
            $[57] = t17;
          } else {
            t17 = $[57];
          }
          let t18;
          if ($[58] !== t) {
            t18 = t("\u062F\u0631 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627\u06CC \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC\u200C\u0634\u062F\u0647 \u0645\u0648\u0631\u062F\u06CC \u0628\u0627 \u0627\u06CC\u0646 \u0648\u0636\u0639\u06CC\u062A \u0646\u06CC\u0633\u062A. \u062F\u0633\u062A\u0647\u200C\u06CC \u062F\u06CC\u06AF\u0631\u06CC \u0631\u0627 \u0627\u0646\u062A\u062E\u0627\u0628 \u06A9\u0646\u06CC\u062F.", "None of the loaded bids has this status. Pick another view.");
            $[58] = t;
            $[59] = t18;
          } else {
            t18 = $[59];
          }
          let t19;
          if ($[60] !== f) {
            t19 = () => f.setTab("all");
            $[60] = f;
            $[61] = t19;
          } else {
            t19 = $[61];
          }
          let t20;
          if ($[62] !== t) {
            t20 = t("\u0647\u0645\u0647\u200C\u06CC \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627", "All bids");
            $[62] = t;
            $[63] = t20;
          } else {
            t20 = $[63];
          }
          let t21;
          if ($[64] !== t19 || $[65] !== t20) {
            t21 = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={t19}>{t20}</button>;
            $[64] = t19;
            $[65] = t20;
            $[66] = t21;
          } else {
            t21 = $[66];
          }
          let t22;
          if ($[67] !== t17 || $[68] !== t18 || $[69] !== t21) {
            t22 = <DeskEmpty tone="filter" icon={t16} title={t17} body={t18} actions={t21} />;
            $[67] = t17;
            $[68] = t18;
            $[69] = t21;
            $[70] = t22;
          } else {
            t22 = $[70];
          }
          body = t22;
        } else {
          const t16 = m.isRefreshing || undefined;
          let t17;
          if ($[71] !== t) {
            t17 = t("\u0648\u0636\u0639\u06CC\u062A", "Status");
            $[71] = t;
            $[72] = t17;
          } else {
            t17 = $[72];
          }
          let t18;
          if ($[73] !== t17) {
            t18 = <span>{t17}</span>;
            $[73] = t17;
            $[74] = t18;
          } else {
            t18 = $[74];
          }
          let t19;
          if ($[75] !== t) {
            t19 = t("\u0645\u0632\u0627\u06CC\u062F\u0647", "Auction");
            $[75] = t;
            $[76] = t19;
          } else {
            t19 = $[76];
          }
          let t20;
          if ($[77] !== t19) {
            t20 = <span>{t19}</span>;
            $[77] = t19;
            $[78] = t20;
          } else {
            t20 = $[78];
          }
          let t21;
          if ($[79] !== t) {
            t21 = t("\u0645\u0628\u0644\u063A", "Amount");
            $[79] = t;
            $[80] = t21;
          } else {
            t21 = $[80];
          }
          let t22;
          if ($[81] !== t21) {
            t22 = <span className="mb-h-num">{t21}</span>;
            $[81] = t21;
            $[82] = t22;
          } else {
            t22 = $[82];
          }
          let t23;
          if ($[83] !== t) {
            t23 = t("\u0632\u0645\u0627\u0646 \u062B\u0628\u062A", "Placed");
            $[83] = t;
            $[84] = t23;
          } else {
            t23 = $[84];
          }
          let t24;
          if ($[85] !== t23) {
            t24 = <span>{t23}</span>;
            $[85] = t23;
            $[86] = t24;
          } else {
            t24 = $[86];
          }
          let t25;
          if ($[87] !== t) {
            t25 = t("\u0634\u0628\u06A9\u0647 \u0648 \u0627\u0631\u0632", "Chain \xB7 currency");
            $[87] = t;
            $[88] = t25;
          } else {
            t25 = $[88];
          }
          let t26;
          if ($[89] !== t25) {
            t26 = <span>{t25}</span>;
            $[89] = t25;
            $[90] = t26;
          } else {
            t26 = $[90];
          }
          let t27;
          if ($[91] !== t) {
            t27 = t("\u0648\u0636\u0639\u06CC\u062A \u0645\u0632\u0627\u06CC\u062F\u0647", "Auction");
            $[91] = t;
            $[92] = t27;
          } else {
            t27 = $[92];
          }
          let t28;
          if ($[93] !== t27) {
            t28 = <span>{t27}</span>;
            $[93] = t27;
            $[94] = t28;
          } else {
            t28 = $[94];
          }
          let t29;
          if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
            t29 = <span />;
            $[95] = t29;
          } else {
            t29 = $[95];
          }
          let t30;
          if ($[96] !== t18 || $[97] !== t20 || $[98] !== t22 || $[99] !== t24 || $[100] !== t26 || $[101] !== t28) {
            t30 = <div className="mb-head" aria-hidden={true}>{t18}{t20}{t22}{t24}{t26}{t28}{t29}</div>;
            $[96] = t18;
            $[97] = t20;
            $[98] = t22;
            $[99] = t24;
            $[100] = t26;
            $[101] = t28;
            $[102] = t30;
          } else {
            t30 = $[102];
          }
          let t31;
          if ($[103] !== lang || $[104] !== m.visible || $[105] !== reveal.visible) {
            let t32;
            if ($[107] !== lang) {
              t32 = b => <MyBidRow key={b.id} bid={b} lang={lang} />;
              $[107] = lang;
              $[108] = t32;
            } else {
              t32 = $[108];
            }
            t31 = m.visible.slice(0, reveal.visible).map(t32);
            $[103] = lang;
            $[104] = m.visible;
            $[105] = reveal.visible;
            $[106] = t31;
          } else {
            t31 = $[106];
          }
          let t32;
          if ($[109] !== t31) {
            t32 = <ol>{t31}</ol>;
            $[109] = t31;
            $[110] = t32;
          } else {
            t32 = $[110];
          }
          let t33;
          if ($[111] !== t16 || $[112] !== t30 || $[113] !== t32) {
            t33 = <div className="mb-ledger" aria-busy={t16}>{t30}{t32}</div>;
            $[111] = t16;
            $[112] = t30;
            $[113] = t32;
            $[114] = t33;
          } else {
            t33 = $[114];
          }
          const t34 = reveal.hasMore;
          const t35 = m.isFetchingMore;
          const t36 = reveal.more;
          const t37 = reveal.sentinel;
          let t38;
          if ($[115] !== t) {
            t38 = t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627\u06CC \u0628\u06CC\u0634\u062A\u0631", "More bids");
            $[115] = t;
            $[116] = t38;
          } else {
            t38 = $[116];
          }
          let t39;
          if ($[117] !== m.isFetchingMore || $[118] !== reveal.hasMore || $[119] !== reveal.more || $[120] !== reveal.sentinel || $[121] !== t38) {
            t39 = <DeskMore show={t34} busy={t35} onMore={t36} sentinel={t37} label={t38} />;
            $[117] = m.isFetchingMore;
            $[118] = reveal.hasMore;
            $[119] = reveal.more;
            $[120] = reveal.sentinel;
            $[121] = t38;
            $[122] = t39;
          } else {
            t39 = $[122];
          }
          let t40;
          if ($[123] !== t33 || $[124] !== t39) {
            t40 = <>{t33}{t39}</>;
            $[123] = t33;
            $[124] = t39;
            $[125] = t40;
          } else {
            t40 = $[125];
          }
          body = t40;
        }
      }
    }
  }
  let t16;
  if ($[126] !== embedded || $[127] !== t) {
    t16 = embedded ? t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627\u06CC \u0645\u0646", "My bids") : undefined;
    $[126] = embedded;
    $[127] = t;
    $[128] = t16;
  } else {
    t16 = $[128];
  }
  let t17;
  if ($[129] !== embedded || $[130] !== t) {
    t17 = embedded ? t("\u0647\u0631 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0628\u0627 \u0648\u0636\u0639\u06CC\u062A\u060C \u0645\u0628\u0644\u063A \u0648 \u0646\u062A\u06CC\u062C\u0647\u200C\u0627\u0634.", "Each bid with its status, amount and outcome.") : undefined;
    $[129] = embedded;
    $[130] = t;
    $[131] = t17;
  } else {
    t17 = $[131];
  }
  let t18;
  if ($[132] !== t) {
    t18 = t("\u062C\u0633\u062A\u200C\u0648\u062C\u0648 \u0648 \u0641\u06CC\u0644\u062A\u0631 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627", "Search and filter your bids");
    $[132] = t;
    $[133] = t18;
  } else {
    t18 = $[133];
  }
  const t19 = f.reset;
  let t20;
  if ($[134] !== t) {
    t20 = t("\u067E\u0627\u06A9 \u06A9\u0631\u062F\u0646 \u0641\u06CC\u0644\u062A\u0631\u0647\u0627", "Clear filters");
    $[134] = t;
    $[135] = t20;
  } else {
    t20 = $[135];
  }
  const t21 = f.filtered;
  const t22 = embedded ? "mb-search-tab" : "mb-search";
  const t23 = f.auctionId;
  const t24 = f.setAuctionId;
  let t25;
  if ($[136] !== t) {
    t25 = t("\u0634\u0646\u0627\u0633\u0647\u200C\u06CC \u0645\u0632\u0627\u06CC\u062F\u0647", "Auction ID");
    $[136] = t;
    $[137] = t25;
  } else {
    t25 = $[137];
  }
  let t26;
  if ($[138] !== t) {
    t26 = t("\u0641\u06CC\u0644\u062A\u0631 \u0628\u0627 \u0634\u0646\u0627\u0633\u0647\u200C\u06CC \u0645\u0632\u0627\u06CC\u062F\u0647", "Filter by auction ID");
    $[138] = t;
    $[139] = t26;
  } else {
    t26 = $[139];
  }
  let t27;
  if ($[140] !== t) {
    t27 = t("\u067E\u0627\u06A9 \u06A9\u0631\u062F\u0646", "Clear");
    $[140] = t;
    $[141] = t27;
  } else {
    t27 = $[141];
  }
  let t28;
  if ($[142] !== f.auctionId || $[143] !== f.setAuctionId || $[144] !== t22 || $[145] !== t25 || $[146] !== t26 || $[147] !== t27) {
    t28 = <SearchField id={t22} value={t23} onChange={t24} placeholder={t25} label={t26} clearLabel={t27} />;
    $[142] = f.auctionId;
    $[143] = f.setAuctionId;
    $[144] = t22;
    $[145] = t25;
    $[146] = t26;
    $[147] = t27;
    $[148] = t28;
  } else {
    t28 = $[148];
  }
  const t29 = f.currencyId || "all";
  let t30;
  if ($[149] !== t) {
    t30 = t("\u0627\u0631\u0632", "Currency");
    $[149] = t;
    $[150] = t30;
  } else {
    t30 = $[150];
  }
  let t31;
  if ($[151] !== f) {
    t31 = v => f.setCurrencyId(v === "all" ? "" : v);
    $[151] = f;
    $[152] = t31;
  } else {
    t31 = $[152];
  }
  let t32;
  if ($[153] !== t) {
    t32 = t("\u0647\u0645\u0647\u200C\u06CC \u0627\u0631\u0632\u0647\u0627", "All currencies");
    $[153] = t;
    $[154] = t32;
  } else {
    t32 = $[154];
  }
  let t33;
  if ($[155] !== t32) {
    t33 = {
      key: "all",
      label: t32
    };
    $[155] = t32;
    $[156] = t33;
  } else {
    t33 = $[156];
  }
  let t34;
  if ($[157] !== m.currencies || $[158] !== t33) {
    t34 = [t33, ...m.currencies];
    $[157] = m.currencies;
    $[158] = t33;
    $[159] = t34;
  } else {
    t34 = $[159];
  }
  let t35;
  if ($[160] !== t29 || $[161] !== t30 || $[162] !== t31 || $[163] !== t34) {
    t35 = <SortMenu value={t29} label={t30} align="start" onChange={t31} options={t34} />;
    $[160] = t29;
    $[161] = t30;
    $[162] = t31;
    $[163] = t34;
    $[164] = t35;
  } else {
    t35 = $[164];
  }
  let t36;
  if ($[165] !== f || $[166] !== t || $[167] !== winOptions) {
    t36 = f.tab === "won" ? <span className="v2-bx-trigger v2-bx-sort mb-locked" aria-disabled={true}><ArrowDownUp aria-hidden={true} /><span className="v2-bx-sort-current">{t("\u0641\u0642\u0637 \u0628\u0631\u0646\u062F\u0647\u200C\u0647\u0627", "Winners only")}</span><ChevronDown aria-hidden={true} /></span> : <SortMenu value={f.winnerFilter || "all"} label={t("\u0648\u0636\u0639\u06CC\u062A \u0628\u0631\u062F", "Winning state")} onChange={v_0 => f.setWinnerFilter(v_0 === "all" ? "" : v_0)} options={winOptions} />;
    $[165] = f;
    $[166] = t;
    $[167] = winOptions;
    $[168] = t36;
  } else {
    t36 = $[168];
  }
  let t37;
  if ($[169] !== t) {
    t37 = t("\u0627\u0632", "From");
    $[169] = t;
    $[170] = t37;
  } else {
    t37 = $[170];
  }
  let t38;
  if ($[171] !== f.fromDate || $[172] !== f.setFromDate || $[173] !== lang || $[174] !== t37) {
    t38 = <DateField label={t37} value={f.fromDate} onChange={f.setFromDate} lang={lang} />;
    $[171] = f.fromDate;
    $[172] = f.setFromDate;
    $[173] = lang;
    $[174] = t37;
    $[175] = t38;
  } else {
    t38 = $[175];
  }
  let t39;
  if ($[176] !== t) {
    t39 = t("\u062A\u0627", "To");
    $[176] = t;
    $[177] = t39;
  } else {
    t39 = $[177];
  }
  let t40;
  if ($[178] !== f.setToDate || $[179] !== f.toDate || $[180] !== lang || $[181] !== t39) {
    t40 = <DateField label={t39} value={f.toDate} onChange={f.setToDate} lang={lang} edge="end" />;
    $[178] = f.setToDate;
    $[179] = f.toDate;
    $[180] = lang;
    $[181] = t39;
    $[182] = t40;
  } else {
    t40 = $[182];
  }
  let t41;
  if ($[183] !== f.filtered || $[184] !== f.reset || $[185] !== t18 || $[186] !== t20 || $[187] !== t28 || $[188] !== t35 || $[189] !== t36 || $[190] !== t38 || $[191] !== t40) {
    t41 = <DeskBar label={t18} onReset={t19} resetLabel={t20} canReset={t21}>{t28}{t35}{t36}{t38}{t40}</DeskBar>;
    $[183] = f.filtered;
    $[184] = f.reset;
    $[185] = t18;
    $[186] = t20;
    $[187] = t28;
    $[188] = t35;
    $[189] = t36;
    $[190] = t38;
    $[191] = t40;
    $[192] = t41;
  } else {
    t41 = $[192];
  }
  const t42 = f.tab;
  const t43 = f.setTab;
  let t44;
  if ($[193] !== t) {
    t44 = t("\u062F\u0633\u062A\u0647\u200C\u06CC \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627", "Bid views");
    $[193] = t;
    $[194] = t44;
  } else {
    t44 = $[194];
  }
  const t45 = "dk-views-end";
  const t46 = f.sortBy;
  let t47;
  if ($[195] !== t) {
    t47 = t("\u062A\u0631\u062A\u06CC\u0628", "Order");
    $[195] = t;
    $[196] = t47;
  } else {
    t47 = $[196];
  }
  let t48;
  if ($[197] !== f) {
    t48 = v_1 => f.setSortBy(v_1);
    $[197] = f;
    $[198] = t48;
  } else {
    t48 = $[198];
  }
  let t49;
  if ($[199] !== t) {
    t49 = t("\u062A\u0627\u0632\u0647\u200C\u062A\u0631\u06CC\u0646", "Newest");
    $[199] = t;
    $[200] = t49;
  } else {
    t49 = $[200];
  }
  let t50;
  if ($[201] !== t49) {
    t50 = {
      key: "newest",
      label: t49
    };
    $[201] = t49;
    $[202] = t50;
  } else {
    t50 = $[202];
  }
  let t51;
  if ($[203] !== t) {
    t51 = t("\u0642\u062F\u06CC\u0645\u06CC\u200C\u062A\u0631\u06CC\u0646", "Oldest");
    $[203] = t;
    $[204] = t51;
  } else {
    t51 = $[204];
  }
  let t52;
  if ($[205] !== t51) {
    t52 = {
      key: "oldest",
      label: t51
    };
    $[205] = t51;
    $[206] = t52;
  } else {
    t52 = $[206];
  }
  let t53;
  if ($[207] !== t) {
    t53 = t("\u0628\u06CC\u0634\u062A\u0631\u06CC\u0646 \u0645\u0628\u0644\u063A", "Highest amount");
    $[207] = t;
    $[208] = t53;
  } else {
    t53 = $[208];
  }
  let t54;
  if ($[209] !== t53) {
    t54 = {
      key: "highest",
      label: t53
    };
    $[209] = t53;
    $[210] = t54;
  } else {
    t54 = $[210];
  }
  let t55;
  if ($[211] !== t) {
    t55 = t("\u06A9\u0645\u062A\u0631\u06CC\u0646 \u0645\u0628\u0644\u063A", "Lowest amount");
    $[211] = t;
    $[212] = t55;
  } else {
    t55 = $[212];
  }
  let t56;
  if ($[213] !== t55) {
    t56 = {
      key: "lowest",
      label: t55
    };
    $[213] = t55;
    $[214] = t56;
  } else {
    t56 = $[214];
  }
  let t57;
  if ($[215] !== t50 || $[216] !== t52 || $[217] !== t54 || $[218] !== t56) {
    t57 = [t50, t52, t54, t56];
    $[215] = t50;
    $[216] = t52;
    $[217] = t54;
    $[218] = t56;
    $[219] = t57;
  } else {
    t57 = $[219];
  }
  let t58;
  if ($[220] !== f.sortBy || $[221] !== t47 || $[222] !== t48 || $[223] !== t57) {
    t58 = <SortMenu align="end" value={t46} label={t47} onChange={t48} options={t57} />;
    $[220] = f.sortBy;
    $[221] = t47;
    $[222] = t48;
    $[223] = t57;
    $[224] = t58;
  } else {
    t58 = $[224];
  }
  const t59 = "dk-count";
  const t60 = "status";
  const t61 = m.isRefreshing ? t("\u062F\u0631 \u062D\u0627\u0644 \u0628\u0647\u200C\u0631\u0648\u0632\u0631\u0633\u0627\u0646\u06CC\u2026", "Refreshing\u2026") : <><bdi>{nf.format(m.total)}</bdi> {t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F", "bids")}</>;
  let t62;
  if ($[225] !== t61) {
    t62 = <span className={t59} role={t60}>{t61}</span>;
    $[225] = t61;
    $[226] = t62;
  } else {
    t62 = $[226];
  }
  let t63;
  if ($[227] !== t58 || $[228] !== t62) {
    t63 = <div className={t45}>{t58}{t62}</div>;
    $[227] = t58;
    $[228] = t62;
    $[229] = t63;
  } else {
    t63 = $[229];
  }
  let t64;
  if ($[230] !== m.narrowed || $[231] !== t) {
    t64 = m.narrowed ? t("\u0627\u06CC\u0646 \u062F\u0633\u062A\u0647 \u0641\u0642\u0637 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627\u06CC \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC\u200C\u0634\u062F\u0647 \u0631\u0627 \u062C\u062F\u0627 \u0645\u06CC\u200C\u06A9\u0646\u062F\u061B \u0628\u0627 \u067E\u0627\u06CC\u06CC\u0646 \u0631\u0641\u062A\u0646\u060C \u0628\u0642\u06CC\u0647 \u0647\u0645 \u0628\u0631\u0631\u0633\u06CC \u0645\u06CC\u200C\u0634\u0648\u0646\u062F.", "This view sorts out the loaded bids only; scroll and the rest are checked too.") : t("\xAB\u062A\u0631\u062A\u06CC\u0628\xBB \u0641\u0642\u0637 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627\u06CC \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC\u200C\u0634\u062F\u0647 \u0631\u0627 \u0645\u0631\u062A\u0628 \u0645\u06CC\u200C\u06A9\u0646\u062F\u060C \u0646\u0647 \u06A9\u0644 \u062A\u0627\u0631\u06CC\u062E\u0686\u0647 \u0631\u0627.", "\u201COrder\u201D sorts the loaded bids, not your whole history.");
    $[230] = m.narrowed;
    $[231] = t;
    $[232] = t64;
  } else {
    t64 = $[232];
  }
  let t65;
  if ($[233] !== t64) {
    t65 = <p className="dk-note">{t64}</p>;
    $[233] = t64;
    $[234] = t65;
  } else {
    t65 = $[234];
  }
  return <div className={embedded ? "dk dk--tab mb" : "v2-page dk mb"}>{embedded ? null : <RouteHero eyebrow={<><History aria-hidden={true} />{t("\u0645\u06CC\u0632 \u06A9\u0627\u0631", "Your desk")}</>} title={t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627\u06CC \u0645\u0646", "My bids")} description={t("\u0647\u0645\u0647\u200C\u06CC \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627\u06CC\u06CC \u06A9\u0647 \u0631\u0648\u06CC \u0645\u0632\u0627\u06CC\u062F\u0647\u200C\u0647\u0627 \u062F\u0627\u062F\u0647\u200C\u0627\u06CC\u062F\u060C \u0628\u0627 \u0648\u0636\u0639\u06CC\u062A \u0648 \u0646\u062A\u06CC\u062C\u0647\u200C\u06CC \u0647\u0631\u06A9\u062F\u0627\u0645.", "Every bid you've placed on an auction, with its status and outcome.")} aside={<RouteHeroMark icon={<History />} value={m.counts.all !== null ? nf.format(m.counts.all) : undefined} caption={t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F", "bids")} />} />}<DeskCover title={t16} lead={t17} tally={tally} />{t41}<div className="dk-views"><DeskSeg value={t42} options={tabs} onChange={t43} label={t44} />{t63}</div>{t65}{body}</div>;
}